import {Box, Card, CardContent, CardHeader} from "@mui/material";
import Grid from "@mui/material/Grid/Grid";
import {useLocalization} from "@smalox/shared";
import {EditForm, TextField} from "@variocube/app-ui";
import React, {useEffect, useState} from "react";
import {UserMutation} from "../../api";

interface UserEditFormProps {
	loading: boolean;
	user?: UserMutation;
	readOnly?: boolean;
	onSave: (mutation: UserMutation) => Promise<any>;
}

export function UserEditForm(props: UserEditFormProps) {
	const {loading, user, readOnly, onSave} = props;
	const {t} = useLocalization();

	const [givenName, setGivenName] = useState("");
	const [familyName, setFamilyName] = useState("");
	const [displayName, setDisplayName] = useState("");
	const [email, setEmail] = useState("");

	useEffect(() => {
		if (user) {
			setGivenName(user.givenName ?? "");
			setFamilyName(user.familyName ?? "");
			setDisplayName(user.displayName ?? "");
			setEmail(user.email ?? "");
		}
	}, [user]);

	async function handleSave() {
		await onSave({
			givenName,
			familyName,
			displayName,
			email,
		});
	}

	// A single disabled fieldset drives read-only for the whole form, so adding a field later cannot
	// accidentally leave an editable input behind. The per-field `disabled` below is only for MUI's greyed
	// styling — the fieldset is what actually enforces read-only, so a new field can never leak as editable.
	const fields = (
		<Box component="fieldset" disabled={readOnly} sx={{border: 0, p: 0, m: 0, minInlineSize: 0}}>
			<CardHeader title={t("users.name")} />
			<CardContent>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} md={4}>
						<TextField
							label={t("users.givenName")}
							value={givenName}
							onChange={setGivenName}
							fullWidth
							required
							disabled={readOnly}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<TextField
							label={t("users.familyName")}
							value={familyName}
							onChange={setFamilyName}
							fullWidth
							required
							disabled={readOnly}
						/>
					</Grid>
					<Grid item xs={12} md={8}>
						<TextField
							label={t("users.displayName")}
							value={displayName}
							onChange={setDisplayName}
							fullWidth
							required
							disabled={readOnly}
						/>
					</Grid>
				</Grid>
			</CardContent>
			<CardHeader title={t("users.email")} />
			<CardContent>
				<Grid container>
					<Grid item xs={12} md={8}>
						<TextField
							label={t("users.email")}
							value={email}
							onChange={setEmail}
							type="email"
							fullWidth
							required
							disabled={readOnly}
						/>
					</Grid>
				</Grid>
			</CardContent>
		</Box>
	);

	if (readOnly) {
		return <Card>{fields}</Card>;
	}

	return (
		<EditForm loading={loading} onSave={handleSave} labels={t}>
			{fields}
		</EditForm>
	);
}
